import React, { useEffect, useMemo, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  notification,
  Row,
  Table,
  Tag,
  type TableProps,
} from 'antd';
import { twMerge } from 'tailwind-merge';
import { DeleteFilled } from '@ant-design/icons';
import locale from 'antd/es/date-picker/locale/th_TH';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { IOrder, ISearchOrderReq, ISearchOrderRes } from '@/interfaces/order';
import { ICustomer } from '@/interfaces/customer';
import { IBillingNote, IBillingNoteUsedOrder } from '@/interfaces/billing-note';
import { searchOrders } from '@/adapters/order';
import {
  addBillingNote,
  getBillingNote,
  getUsedBillingOrders,
  updateBillingNote,
} from '@/adapters/billing-note';
import CustomerSelector from '@/components/selector/customer-selector';
import Title from '@/components/title';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { getOrderStatusName, getOrderStatusColor } from '@/helpers/orderHelper';
import { EOrderStatus } from '@/enums/EOrder';
import { useAuth } from '@/contexts/AuthContext';

const { Search } = Input;

interface IBillingNoteForm {
  customerId: number;
  docDate: Dayjs;
  remark?: string;
}

const _orderCriteria: Partial<ISearchOrderReq> = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const CreateBillingNote = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { userInfo } = useAuth();
  const [mainForm] = Form.useForm<IBillingNoteForm>();
  const [customerId, setCustomerId] = useState<number>(0);
  const [selectedOrders, setSelectedOrders] = useState<IOrder[]>([]);
  const [criteria, setCriteria] = useState<Partial<ISearchOrderReq>>({
    ..._orderCriteria,
  });

  const { data: billingNote } = useQuery<IBillingNote, Error>({
    queryKey: ['get-billing-note', id],
    queryFn: async () => await getBillingNote(Number(id)),
    enabled: isEdit,
  });

  useEffect(() => {
    if (billingNote) {
      setCustomerId(Number(billingNote.customerId || 0));
      mainForm.setFieldsValue({
        customerId: billingNote.customerId,
        docDate: dayjs(billingNote.docDate),
        remark: billingNote.remark,
      });
      setSelectedOrders(
        (billingNote.billingNoteItems || []).map(
          item =>
            ({
              id: item.orderId,
              code: item.orderCode,
              ivNo: item.ivNo,
              soNo: item.soNo,
              poNo: item.poNo,
              orderDate: item.orderDate,
              dueDate: item.dueDate,
              summary: Number(item.amount || 0),
            }) as IOrder,
        ),
      );
    }
  }, [billingNote]);

  const { data: orders, isLoading } = useQuery<
    AxiosResponse<ISearchOrderRes>,
    Error
  >({
    queryKey: ['search-orders-billing-note', criteria, customerId],
    queryFn: async () =>
      await searchOrders({
        ...criteria,
        customerId,
      } as ISearchOrderReq),
    enabled: !!customerId,
  });

  // orders already used by another active billing note (cannot be selected)
  const { data: usedOrders } = useQuery<
    AxiosResponse<IBillingNoteUsedOrder[]>,
    Error
  >({
    queryKey: ['billing-note-used-orders', customerId, id],
    queryFn: async () =>
      await getUsedBillingOrders(customerId, isEdit ? Number(id) : undefined),
    enabled: !!customerId,
  });

  const usedOrderMap = useMemo(() => {
    const map = new Map<number, IBillingNoteUsedOrder>();
    (usedOrders?.data || []).forEach(u => map.set(u.orderId, u));
    return map;
  }, [usedOrders]);

  const onCustomerChange = (customer: ICustomer) => {
    const id = Number(customer?.id || 0);
    setCustomerId(id);
    setSelectedOrders([]);
    setCriteria({ ..._orderCriteria });
    mainForm.setFieldsValue({ customerId: id || undefined });
  };

  const selectedRowKeys = useMemo(
    () => selectedOrders.map(o => Number(o.id)),
    [selectedOrders],
  );

  const onSelectChange = (_: React.Key[], rows: IOrder[]) => {
    // keep selections across pages: merge current page rows with previous selections
    const currentPageIds = orders?.data?.data?.map(o => Number(o.id)) || [];
    const keptFromOtherPages = selectedOrders.filter(
      o => !currentPageIds.includes(Number(o.id)),
    );
    const merged = [...keptFromOtherPages, ...rows.filter(Boolean)];
    // unique by id
    const unique = merged.filter(
      (o, idx) => merged.findIndex(m => m.id === o.id) === idx,
    );
    setSelectedOrders(unique);
  };

  const removeSelected = (id: number) => {
    setSelectedOrders(selectedOrders.filter(o => Number(o.id) !== id));
  };

  const orderColumns: TableProps<IOrder>['columns'] = [
    {
      title: 'เลขที่ออเดอร์ (OR)',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      align: 'center',
      width: 130,
    },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'CO No.',
      dataIndex: 'soNo',
      key: 'soNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'วันที่ส่งสินค้า',
      dataIndex: 'orderDate',
      key: 'orderDate',
      align: 'center',
      width: 110,
      render: _ => formatDate(_),
    },
    {
      title: 'ครบกำหนด',
      dataIndex: 'dueDate',
      key: 'dueDate',
      align: 'center',
      width: 110,
      render: _ => formatDate(_),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 110,
      render: (_: EOrderStatus, rec) => {
        const color = getOrderStatusColor(_, rec?.transportation);
        return (
          <div
            className={twMerge(
              'truncate rounded py-0.5',
              color?.bg,
              color?.text,
            )}
          >
            {getOrderStatusName(_, rec?.transportation)}
          </div>
        );
      },
    },
    {
      title: 'จำนวนเงิน',
      dataIndex: 'summary',
      key: 'summary',
      align: 'right',
      width: 120,
      className: 'font-bold',
      render: (_: number) => formatNumber(_ || 0, 2),
    },
    {
      title: 'วางบิล',
      key: 'billed',
      align: 'center',
      width: 130,
      render: (_, rec) => {
        const used = usedOrderMap.get(Number(rec.id));
        if (!used) return null;
        return (
          <Tag color="orange" className="text-center">
            {`วางบิลแล้ว ${used.billingNoteCode}`}
          </Tag>
        );
      },
    },
  ];

  const selectedColumns: TableProps<IOrder>['columns'] = [
    {
      title: 'No.',
      key: 'no',
      align: 'center',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'เลขที่ออเดอร์ (OR)',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      align: 'center',
      width: 130,
    },
    {
      title: 'เลขที่ใบกำกับ (IV)',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 130,
    },
    {
      title: 'วันที่',
      dataIndex: 'orderDate',
      key: 'orderDate',
      align: 'center',
      width: 110,
      render: _ => formatDate(_),
    },
    {
      title: 'ครบกำหนด',
      dataIndex: 'dueDate',
      key: 'dueDate',
      align: 'center',
      width: 110,
      render: _ => formatDate(_),
    },
    {
      title: 'จำนวนเงิน',
      dataIndex: 'summary',
      key: 'summary',
      align: 'right',
      width: 120,
      className: 'font-bold',
      render: (_: number) => formatNumber(_ || 0, 2),
    },
    {
      key: 'action',
      width: 40,
      align: 'center',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700 px-1.5 cursor-pointer"
          onClick={() => removeSelected(Number(rec.id))}
        />
      ),
    },
  ];

  const totalAmount = selectedOrders.reduce(
    (sum, o) => sum + Number(o.summary || 0),
    0,
  );

  const mutation = useMutation({
    mutationFn: async (data: Partial<IBillingNote>) =>
      isEdit ? await updateBillingNote(data) : await addBillingNote(data),
    mutationKey: ['save-billing-note'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: isEdit ? 'แก้ไขใบวางบิลสำเร็จ' : 'บันทึกใบวางบิลสำเร็จ',
        });
        const savedId = isEdit ? Number(id) : (data?.data as IBillingNote)?.id;
        navigate(`/billing-note/${savedId}`);
      }
    },
    onError: error => {
      console.log(error);
      notification.error({
        message: isEdit ? 'แก้ไขใบวางบิลไม่สำเร็จ' : 'บันทึกใบวางบิลไม่สำเร็จ',
      });
    },
  });

  const handleSave = async () => {
    mainForm
      .validateFields()
      .then(async (values: IBillingNoteForm) => {
        if (selectedOrders.length === 0) {
          message.warning('เลือกออเดอร์อย่างน้อย 1 รายการ');
          return;
        }
        const payload: Partial<IBillingNote> = {
          id: isEdit ? Number(id) : undefined,
          customerId: values.customerId,
          businessUnitId:
            Number(selectedOrders[0]?.businessUnitId || 0) ||
            billingNote?.businessUnitId ||
            userInfo?.bu ||
            undefined,
          docDate: dayjs(values.docDate).toDate(),
          remark: values.remark,
          billingNoteItems: selectedOrders.map(o => ({
            orderId: Number(o.id),
            amount: Number(o.summary || 0),
          })),
        };
        await mutation.mutateAsync(payload);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/billing-note`)}
              >
                ใบวางบิล
              </div>
            ),
          },
          {
            title: isEdit ? `แก้ไข ${billingNote?.code || ''}` : 'สร้างใหม่',
          },
        ]}
      />
      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          autoComplete="off"
          initialValues={{
            docDate: dayjs(),
          }}
        >
          <Row gutter={24}>
            <Col md={24} lg={16} xxl={18}>
              <Row gutter={12}>
                <Col xs={24} md={10}>
                  <Form.Item<IBillingNoteForm>
                    label="ลูกค้า"
                    name="customerId"
                    rules={[{ required: true, message: 'เลือกลูกค้า' }]}
                  >
                    <CustomerSelector
                      handleSelectData={onCustomerChange}
                      customer={isEdit ? billingNote?.customer : undefined}
                      readonly={isEdit}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={14}>
                  <Form.Item<IBillingNoteForm> label="หมายเหตุ" name="remark">
                    <Input size="large" placeholder="หมายเหตุ" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col md={24} lg={8} xxl={6}>
              <Row gutter={12}>
                <Col xs={24} md={{ span: 12, offset: 12 }}>
                  <Form.Item<IBillingNoteForm>
                    label="วันที่วางบิล"
                    name="docDate"
                    rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
                  >
                    <DatePicker
                      allowClear={false}
                      size="large"
                      className="w-full"
                      locale={locale}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
      {!!customerId && (
        <>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`เลือกออเดอร์`}
          />
          <div className="flex flex-col gap-3">
            <Search
              className="md:max-w-[400px]"
              placeholder="ค้นหาออเดอร์"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={text =>
                setCriteria({
                  ...criteria,
                  search: text,
                  pageStart: 1,
                })
              }
            />
            <Table<IOrder>
              size="small"
              bordered
              rowKey={rec => Number(rec.id)}
              loading={isLoading}
              dataSource={orders?.data?.data || []}
              columns={orderColumns}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                onChange: onSelectChange,
                getCheckboxProps: rec => ({
                  disabled:
                    rec.status === EOrderStatus.CANCEL ||
                    usedOrderMap.has(Number(rec.id)),
                }),
              }}
              pagination={{
                current: criteria.pageStart,
                pageSize: criteria.pageSize,
                total: orders?.data?.totalRecords || 0,
                showSizeChanger: false,
                onChange: (page, pageSize) =>
                  setCriteria({
                    ...criteria,
                    pageStart: page,
                    pageSize,
                  }),
              }}
              scroll={{ x: 900 }}
            />
          </div>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการวางบิล (${selectedOrders.length} รายการ)`}
          />
          <Table<IOrder>
            size="small"
            bordered
            rowKey={rec => Number(rec.id)}
            locale={{
              emptyText: 'เลือกออเดอร์เพื่อเพิ่มรายการวางบิล',
            }}
            dataSource={selectedOrders}
            columns={selectedColumns}
            pagination={false}
            scroll={{ x: 800 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell
                    index={0}
                    colSpan={5}
                    className="font-bold"
                    align="right"
                  >
                    รวมเงินทั้งสิ้น
                  </Table.Summary.Cell>
                  <Table.Summary.Cell
                    index={5}
                    colSpan={1}
                    className="font-bold"
                    align="right"
                  >
                    {formatNumber(totalAmount, 2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} colSpan={1}>
                    {''}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <div className="flex justify-end mt-5 gap-2">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="font-bold min-w-[150px]"
              loading={mutation.isPending}
              onClick={() => handleSave()}
              disabled={selectedOrders.length === 0}
            >
              บันทึก
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default CreateBillingNote;
